import React from "react";
import { Check, Mail, Trash2, RefreshCw, X, ChevronDown, ChevronUp, Upload, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { formatTime12h, getTodayString } from "../eventTime";
import { SortHeader } from "../SortHeader";

export const CalendarImportsTab = (props) => {
  const {
    events,
    scrapedEvents,
    scanning,
    sortField,
    sortDir,
    expandedRows,
    tableRef,
    uploading,
    fileInputRef,
    handleCSVUpload,
    handleRefreshEvents,
    handleClearAllScrapedEvents,
    handleUpdateScrapedStatus,
    handleDeleteScrapedEvent,
    toggleRowExpand,
    handleSort,
    getSortedEvents,
  } = props;
  return (
          <TabsContent value="calendar-imports" data-testid="calendar-imports-content">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-zinc-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-crew-yellow">CALENDAR IMPORTS</h2>
                  <p className="text-zinc-500 text-sm">
                    {scrapedEvents.length} events from calendar invitations • Click column headers to sort
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* CSV Upload */}
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    ref={fileInputRef}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    variant="outline"
                    size="sm"
                    className="border-crew-yellow text-crew-yellow hover:bg-crew-yellow hover:text-black text-xs"
                    data-testid="upload-csv-btn"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={14} className="mr-1" /> Upload CSV
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleClearAllScrapedEvents}
                    variant="outline"
                    size="sm"
                    className="border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white text-xs"
                  >
                    <Trash2 size={14} className="mr-1" /> Clear All
                  </Button>
                  <Button
                    onClick={handleRefreshEvents}
                    disabled={scanning}
                    className="bg-crew-yellow text-black font-bold hover:bg-white text-sm"
                  >
                    <RefreshCw className={`mr-1 h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
                    {scanning ? 'Scanning...' : 'Refresh'}
                  </Button>
                </div>
              </div>
              
              {/* CSV Format Helper */}
              <div className="p-3 bg-zinc-900/50 border-b border-zinc-800 text-xs text-zinc-500">
                <FileSpreadsheet size={12} className="inline mr-1" />
                CSV columns: <span className="text-zinc-400">Event Name, Date, Start Time, Call Time, End Time, Location, Notes</span>
              </div>

              {scrapedEvents.length === 0 ? (
                <div className="p-12 text-center text-zinc-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No events found. Click "Refresh" to scan emails or "Upload CSV" to import.</p>
                </div>
              ) : (
                <div className="overflow-x-auto overflow-y-auto max-h-[600px]" ref={tableRef}>
                  <table className="w-full text-sm border-collapse min-w-[1300px]">
                    <thead className="bg-zinc-900 sticky top-0 z-10">
                      <tr className="border-b border-zinc-700">
                        <SortHeader field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Status</SortHeader>
                        <SortHeader field="event_date" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Date</SortHeader>
                        <SortHeader field="start_time" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Start</SortHeader>
                        <SortHeader field="call_time" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Crew Call</SortHeader>
                        <SortHeader field="end_time" sortField={sortField} sortDir={sortDir} onSort={handleSort}>End</SortHeader>
                        <SortHeader field="name" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Event Name</SortHeader>
                        <SortHeader field="location" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Location</SortHeader>
                        <th className="py-3 px-3 text-left text-xs font-bold uppercase text-zinc-400 w-12">Notes</th>
                        <th className="py-3 px-3 text-right text-xs font-bold uppercase text-zinc-400 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {getSortedEvents().map((event, idx) => {
                        const isExpanded = expandedRows[event.id];
                        const isCancelled = event.status === 'cancelled';
                        const today = getTodayString();
                        const isToday = event.event_date === today;
                        const isPast = event.event_date < today;
                        
                        return (
                          <React.Fragment key={event.id}>
                            {/* Main Row */}
                            <tr className={`hover:bg-zinc-900/50 ${isCancelled ? 'bg-red-900/10' : ''} ${isToday ? 'bg-crew-yellow/10 border-l-4 border-l-crew-yellow' : ''} ${isPast && !isCancelled ? 'opacity-60' : ''}`}>
                              {/* Status */}
                              <td className="py-2 px-3 w-16">
                                <button
                                  onClick={() => handleUpdateScrapedStatus(event.id, isCancelled ? 'confirmed' : 'cancelled')}
                                  className="flex items-center gap-2 group"
                                  title={`Click to mark as ${isCancelled ? 'confirmed' : 'cancelled'}`}
                                >
                                  {isCancelled ? (
                                    <div className="w-6 h-6 rounded bg-red-500/20 flex items-center justify-center group-hover:bg-red-500/40">
                                      <X className="w-4 h-4 text-red-500" />
                                    </div>
                                  ) : (
                                    <div className="w-6 h-6 rounded bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/40">
                                      <Check className="w-4 h-4 text-green-500" />
                                    </div>
                                  )}
                                </button>
                              </td>
                              
                              {/* Event Date */}
                              <td className="py-2 px-3 w-28">
                                <div className="flex flex-col">
                                  <span className={`font-mono text-sm ${isCancelled ? 'text-red-400 line-through' : isToday ? 'text-crew-yellow font-bold' : 'text-crew-yellow font-bold'}`}>
                                    {event.event_date || '—'}
                                  </span>
                                  {isToday && <span className="text-xs text-crew-yellow font-bold">TODAY</span>}
                                  {isPast && !isCancelled && <span className="text-xs text-zinc-500">PAST</span>}
                                </div>
                              </td>
                              
                              {/* Start Time */}
                              <td className="py-2 px-3 w-24">
                                <span className={`font-mono text-zinc-300 text-sm ${isCancelled ? 'line-through opacity-60' : ''}`}>
                                  {formatTime12h(event.start_time)}
                                </span>
                              </td>
                              
                              {/* Crew Call Time */}
                              <td className="py-2 px-3 w-24">
                                <span className={`font-mono text-crew-yellow text-sm font-bold ${isCancelled ? 'line-through opacity-60' : ''}`}>
                                  {formatTime12h(event.call_time) || '—'}
                                </span>
                              </td>
                              
                              {/* End Time */}
                              <td className="py-2 px-3 w-24">
                                <span className={`font-mono text-zinc-300 text-sm ${isCancelled ? 'line-through opacity-60' : ''}`}>
                                  {formatTime12h(event.end_time)}
                                </span>
                              </td>
                              
                              {/* Event Name */}
                              <td className="py-2 px-3">
                                <div className={`font-semibold ${isCancelled ? 'text-red-400 line-through' : 'text-white'}`}>
                                  {event.name}
                                </div>
                                {isCancelled && (
                                  <span className="text-xs text-red-400 font-bold">CANCELLED</span>
                                )}
                                {(event.livestream_youtube || event.recording_required) && !isCancelled && (
                                  <div className="flex flex-wrap gap-1 mt-1" data-testid={`import-av-badges-${idx}`}>
                                    {event.livestream_youtube && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">● LIVESTREAM (YouTube)</span>
                                    )}
                                    {event.recording_required && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">● RECORDING</span>
                                    )}
                                  </div>
                                )}
                              </td>
                              
                              {/* Location */}
                              <td className="py-2 px-3 w-28">
                                <span className={`text-zinc-400 text-sm ${isCancelled ? 'line-through opacity-60' : ''}`}>
                                  {event.location || '—'}
                                </span>
                              </td>
                              
                              {/* Notes Expand Button */}
                              <td className="py-2 px-3">
                                {event.notes || event.email_content ? (
                                  <Button
                                    onClick={() => toggleRowExpand(event.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-zinc-500 hover:text-crew-yellow h-7 px-2"
                                  >
                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                  </Button>
                                ) : (
                                  <span className="text-zinc-600">—</span>
                                )}
                              </td>
                              
                              {/* Actions */}
                              <td className="py-2 px-3 text-right">
                                <Button
                                  onClick={() => handleDeleteScrapedEvent(event.id)}
                                  variant="ghost"
                                  size="icon"
                                  className="text-zinc-600 hover:text-red-500 h-7 w-7"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </td>
                            </tr>
                            
                            {/* Expanded Notes Row */}
                            {isExpanded && (event.notes || event.email_content) && (
                              <tr className="bg-zinc-900/50">
                                <td colSpan={9} className="p-0">
                                  <div className="p-4 border-t border-zinc-800">
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-2">Event Notes / Details:</div>
                                    <div className="bg-black/50 rounded border border-zinc-800 p-4 max-h-60 overflow-y-auto">
                                      <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed">
                                        {event.notes || event.email_content || 'No details available'}
                                      </pre>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
  );
};
